import { MouseEvent, useState } from 'react';
import { formatClock } from './unitsFormat';

export interface ChartPoint {
  /** Milliseconds since the epoch. */
  at: number;
  value: number;
}

interface UsageChartProps {
  title: string;
  points: ChartPoint[];
  format: (value: number) => string;
  /** A dashed line at the unit's limit, such as its cores; the scale reaches it. */
  limit?: number;
}

const Width = 360;
const Height = 128;
const Left = 40;
const Right = 8;
const Top = 18;
const Bottom = 18;
const ShortestGapMs = 5 * 60_000;

/**
 * The spacing above which two consecutive points are not joined, the unit having written no sample between them:
 * 5 minutes, or 3 times the median spacing of the points when each stands for a longer stretch of samples.
 */
export function gapMs(points: ChartPoint[]): number {
  const spacings = points
    .slice(1)
    .map((point, index) => point.at - points[index].at)
    .sort((a, b) => a - b);
  const median = spacings.length === 0 ? 0 : spacings[Math.floor((spacings.length - 1) / 2)];
  return Math.max(ShortestGapMs, 3 * median);
}

function linePath(points: ChartPoint[], x: (at: number) => number, y: (value: number) => number) {
  const gap = gapMs(points);
  return points
    .map((point, index) => {
      const joined = index > 0 && point.at - points[index - 1].at <= gap;
      const at = `${x(point.at).toFixed(1)},${y(point.value).toFixed(1)}`;
      return joined ? `L${at}` : `M${at} l0,0`;
    })
    .join(' ');
}

const ScaleSteps = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];

/** The smallest round number, a step of `ScaleSteps` times a power of ten, that is not below `value`. */
export function scaleTop(value: number): number {
  if (!(value > 0)) return 1;
  const power = 10 ** Math.floor(Math.log10(value));
  return ScaleSteps.map((step) => step * power).find((top) => top >= value) ?? 10 * power;
}

/** One measure of a unit over time, from its first point to its last, with the value under the pointer. */
export default function UsageChart({ title, points, format, limit }: UsageChartProps) {
  const [hovered, setHovered] = useState<ChartPoint | null>(null);
  const first = points[0]?.at ?? 0;
  const last = points[points.length - 1]?.at ?? first;
  const span = Math.max(last - first, 1);
  const top = scaleTop(Math.max(limit ?? 0, ...points.map((point) => point.value)) * 1.05);
  const x = (at: number) => Left + ((at - first) / span) * (Width - Left - Right);
  const y = (value: number) => Top + (1 - value / top) * (Height - Top - Bottom);

  const onMove = (event: MouseEvent<SVGSVGElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    const at = first + ((((event.clientX - box.left) / box.width) * Width - Left) / (Width - Left - Right)) * span;
    const nearest = points.reduce((best, point) => (Math.abs(point.at - at) < Math.abs(best.at - at) ? point : best), points[0]);
    setHovered(nearest ?? null);
  };

  return (
    <figure style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <figcaption className="muted" style={{ fontSize: 11.5 }}>{title}</figcaption>
      <svg
        role="img"
        aria-label={`${title}: ${points.length} points, highest ${format(Math.max(...points.map((point) => point.value), 0))}`}
        viewBox={`0 0 ${Width} ${Height}`}
        style={{ width: '100%', height: 'auto', display: 'block' }}
        onMouseMove={points.length > 0 ? onMove : undefined}
        onMouseLeave={() => setHovered(null)}
      >
        <line x1={Left} x2={Width - Right} y1={y(0)} y2={y(0)} stroke="var(--line-3)" strokeWidth={1} />
        <text x={Left - 6} y={y(0) + 3.5} textAnchor="end" fontSize={10} fill="var(--text-5)">0</text>
        <text x={Left - 6} y={Top + 3.5} textAnchor="end" fontSize={10} fill="var(--text-5)">{format(top)}</text>
        <line x1={Left} x2={Width - Right} y1={Top} y2={Top} stroke="var(--line)" strokeWidth={1} />
        {limit != null && (
          <g>
            <line x1={Left} x2={Width - Right} y1={y(limit)} y2={y(limit)} stroke="var(--text-6)" strokeWidth={1} strokeDasharray="4 3" />
            <text x={Width - Right} y={y(limit) - 4} textAnchor="end" fontSize={10} fill="var(--text-5)">limit {format(limit)}</text>
          </g>
        )}
        <path d={linePath(points, x, y)} fill="none" stroke="var(--accent)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {points.length > 0 && (
          <>
            <text x={Left} y={Height - 4} fontSize={10} fill="var(--text-5)">{formatClock(new Date(first).toISOString())}</text>
            <text x={Width - Right} y={Height - 4} textAnchor="end" fontSize={10} fill="var(--text-5)">
              {formatClock(new Date(last).toISOString())}
            </text>
          </>
        )}
        {hovered && (
          <g>
            <line x1={x(hovered.at)} x2={x(hovered.at)} y1={Top} y2={y(0)} stroke="var(--line-strong)" strokeWidth={1} />
            <circle cx={x(hovered.at)} cy={y(hovered.value)} r={4} fill="var(--accent)" stroke="var(--surface)" strokeWidth={2} />
            <text x={Left} y={11} fontSize={10.5} fill="var(--text-2)">
              {formatClock(new Date(hovered.at).toISOString())} · {format(hovered.value)}
            </text>
          </g>
        )}
      </svg>
    </figure>
  );
}
